import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useRouter } from "next/router";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import SiteLayout from "~/components/layout/SiteLayout";
import Button from "~/components/common/Button";
import ConfirmModal from "~/components/common/ConfirmModal";
import Toast from "~/components/common/Toast";
import ProjectPopup from "~/components/projects/ProjectPopup";
import PromotionModal from "~/components/projects/PromotionModal";
import styles from "~/components/projects/projects.module.css";
import {
  getProjectRankings,
  getProjectStats,
  getProject,
  getPromotedProjects,
  upvoteProject,
  recordProjectView,
  recordProjectImpressions,
  getProjects,
} from "~/services/projects";
import { getTagGroups } from "~/services/tags";
import { showAppModal, setLoginRedirect } from "~/redux/actions/app";
import { isWechatBrowser } from "~/utils/wechat";
import {
  getProjectTypeLabel,
  hasCustomProjectLogo,
} from "~/utils/project";
import type {
  PlazaProject,
  PlazaStats,
  PromotedProject,
  Project,
} from "~/types/project";

type Filters = {
  field_tag_id?: number;
  project_type?: "personal" | "company";
  open_source?: boolean;
};

/* ===== 小工具 ===== */

const UpArrow = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 2.6c.4 0 .8.17 1.08.46l7.6 7.9c.62.64.16 1.72-.73 1.72H15.6v7.8c0 .84-.68 1.52-1.52 1.52H9.92c-.84 0-1.52-.68-1.52-1.52v-7.8H4.05c-.89 0-1.35-1.08-.73-1.72l7.6-7.9c.28-.29.68-.46 1.08-.46Z" />
  </svg>
);

const RuleArrow = () => (
  <i className={styles.upIn}>
    <UpArrow />
  </i>
);

// 「付费置顶」标识（对齐设计稿 .flag.flag-top，排行榜行与推广卡片同款）
const FlagTop = () => (
  <span className={styles.flag}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 4h6l-1 6 3 3H7l3-3-1-6Z" />
      <path d="M12 16v5" />
    </svg>
    付费置顶
  </span>
);

// 「访问项目」外链箭头（对齐设计稿 .arr-r）
const ArrRight = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 4h6v6" />
    <path d="M20 4 12 12" />
    <path d="M18 14v5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10" />
  </svg>
);

// 项目链接是否可跳转：只有 http(s) 网址算（填小程序名或一段文字的不能跳）
const isJumpableUrl = (url?: string | null) => /^https?:\/\//i.test(url || "");

const formatNum = (n?: number) =>
  n == null ? "—" : n.toLocaleString("zh-CN");

// 相对时间（对齐设计稿「N 天前」）：不带 date-fns zhCN 的「大约/超过」前缀
const formatRelativeTime = (date?: string) => {
  if (!date) return "—";
  const diffMs = Date.now() - new Date(date).getTime();
  if (diffMs < 60 * 1000) return "刚刚";
  const min = Math.floor(diffMs / (60 * 1000));
  if (min < 60) return `${min} 分钟前`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月前`;
  return `${Math.floor(days / 365)} 年前`;
};

const formatWan = (n?: number) => {
  if (n == null) return "—";
  if (n >= 10000) {
    const w = Math.round((n / 10000) * 10) / 10;
    return `${w}w`;
  }
  return n.toLocaleString("zh-CN");
};

/* ===== 项目 logo（列表 / 弹窗 / 我的项目 保持一致） ===== */

const ProjectAvatar = ({
  project,
  size,
  top = false,
}: {
  /** 只用到名称与 logo，排行榜行 / 推广卡片 / 我的项目共用 */
  project: Pick<Project, "name" | "logo_url">;
  size: "lr" | "mine" | "pop" | "pc";
  top?: boolean;
}) => {
  const letter = (project.name || "?")[0];
  const custom = hasCustomProjectLogo(project.logo_url);
  const sizeClass =
    size === "lr"
      ? styles.avatarLr
      : size === "pop"
        ? styles.avatarPop
        : size === "pc"
          ? styles.avatarPc
          : styles.avatarMine;
  if (custom) {
    return (
      <span className={`${styles.avatar} ${sizeClass}`}>
        <img
          className={styles.avatarImg}
          src={project.logo_url}
          alt=""
        />
      </span>
    );
  }
  return (
    <span
      className={`${styles.avatar} ${sizeClass} ${
        top ? styles.avatarT3 : styles.avatarLine
      }`}
    >
      {letter}
    </span>
  );
};

/* ===== 下载App / 公众号 卡（内容参考现成 WechatGroupCard） ===== */

const DownloadAppCard = () => {
  const [tab, setTab] = useState<"app" | "wechat">("app");
  return (
    <div className={`${styles.card} ${styles.cardPad}`}>
      <div className={styles.tabsSm}>
        <span
          className={tab === "app" ? styles.isOn : undefined}
          onClick={() => setTab("app")}
        >
          下载App
        </span>
        <span
          className={tab === "wechat" ? styles.isOn : undefined}
          onClick={() => setTab("wechat")}
        >
          公众号
        </span>
      </div>
      <div className={styles.qrRow}>
        <span className={styles.qr}>
          {tab === "app" ? (
            <img
              src="https://duckfiles.oss-cn-qingdao.aliyuncs.com/eleduck/assets/app.png"
              alt="下载 App"
            />
          ) : (
            <img src="/static/qrcode.png" alt="公众号二维码" />
          )}
        </span>
        <div className={styles.qrText}>
          {tab === "app" ? (
            <>
              <b>
                <a
                  href="/app"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "inherit" }}
                >
                  下载 APP
                </a>
              </b>
              <br />
              以便及时收到回复或进展
            </>
          ) : (
            <>
              <b>关注公众号</b>
              <br />
              订阅每周远程职位
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ===== 快捷入口卡（链接参考现成 SiderNavigationCard） ===== */

const QuickLinksCard = () => (
  <div className={`${styles.card} ${styles.quick}`}>
    <a
      className={styles.quickItem}
      href="https://eleduck.com/posts/6GzfGe"
      target="_blank"
      rel="noreferrer"
    >
      <span className={styles.quickIco}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <path d="M5 4h11l3 3v13H5z" />
          <path d="M8 10h8M8 14h5" />
        </svg>
      </span>
      社区规则
    </a>
    <a
      className={styles.quickItem}
      href="https://eleduck.com/posts/MZXfoe"
      target="_blank"
      rel="noreferrer"
    >
      <span className={styles.quickIco}>
        <img src="/static/logo.png" alt="" />
      </span>
      关于电鸭
    </a>
    <a
      className={styles.quickItem}
      href="https://eleduck.com/posts/z1f0Kp"
      target="_blank"
      rel="noreferrer"
    >
      <span className={styles.quickIco}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        >
          <path d="m12 4 2.4 5 5.6.8-4 3.9 1 5.5-5-2.7-5 2.7 1-5.5-4-3.9 5.6-.8Z" />
        </svg>
      </span>
      人才推荐
    </a>
  </div>
);

/* ===== 页面 ===== */

type PageProps = {
  user: any;
  token: string;
  showAppModal: (name: string, params?: any) => void;
  setLoginRedirect: (url: string | null) => void;
};

type ConfirmState = {
  title: string;
  content?: ReactNode;
  okText: string;
  onOk: () => any;
};

const Page = ({
  token,
  showAppModal,
  setLoginRedirect,
}: PageProps) => {
  const router = useRouter();
  const [hotList, setHotList] = useState<PlazaProject[] | null>(null);
  const [latestList, setLatestList] = useState<PlazaProject[] | null>(null);
  const [hotNext, setHotNext] = useState<number | null>(null);
  const [latestNext, setLatestNext] = useState<number | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [fieldOptions, setFieldOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [stats, setStats] = useState<PlazaStats | null>(null);
  const [mineProjects, setMineProjects] = useState<Project[] | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterShut, setFilterShut] = useState(false);
  // 手机版窄屏放不下两列，「最热 / 最新」二选一，默认最热（PC 上不生效）
  const [mobileCol, setMobileCol] = useState<"hot" | "latest">("hot");
  const [popupProject, setPopupProject] = useState<
    PlazaProject | PromotedProject | null
  >(null);
  const [popupTop, setPopupTop] = useState(false);
  // 弹窗内「已顶过」状态：来自后端详情返回的 upvoted 字段
  const [popupUpvoted, setPopupUpvoted] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [upvoting, setUpvoting] = useState(false);
  // 置顶推广中区块与弹窗
  const [promoted, setPromoted] = useState<PromotedProject[] | null>(null);
  const [boostOpen, setBoostOpen] = useState(false);
  const [boostInitialId, setBoostInitialId] = useState<string | undefined>(
    undefined
  );
  // 入口前置判断的引导弹窗（去发布 / 去打开展示）
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirming, setConfirming] = useState(false);
  // 当前弹窗打开的项目 id，用于丢弃过期详情响应
  const popupIdRef = useRef<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const pendingUpvote = useRef<string | null>(null);

  const showToast = (text: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = window.setTimeout(() => setToast(null), 2000);
  };

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    []
  );

  // 曝光上报是「尽力而为」：失败不影响页面，也不重试
  const reportImpressions = (tokens: (string | undefined)[]) => {
    const list = tokens.filter(Boolean) as string[];
    if (!list.length) return;
    void recordProjectImpressions(list, token).catch(() => {});
  };

  const loadPromoted = () => {
    getPromotedProjects()
      .then((res) => {
        setPromoted(res.promotions || []);
        // 推广位的曝光单独计次（令牌内位置为 promoted）
        reportImpressions([res.impression_token]);
      })
      .catch(() => setPromoted([]));
  };

  const loadAll = async (nextFilters: Filters) => {
    try {
      const [hot, latest] = await Promise.all([
        getProjectRankings({ sort: "hot", page: 1, ...nextFilters }),
        getProjectRankings({ sort: "latest", page: 1, ...nextFilters }),
      ]);
      setHotList(hot.projects);
      setLatestList(latest.projects);
      setHotNext(hot.pager?.next_page ?? null);
      setLatestNext(latest.pager?.next_page ?? null);
      // 一次页面加载合并成一次上报（限流按请求数计）
      reportImpressions([hot.impression_token, latest.impression_token]);
    } catch (err: any) {
      setHotList([]);
      setLatestList([]);
      showToast(err?.message || "项目加载失败，请稍后重试");
    }
  };

  useEffect(() => {
    loadAll({});
    loadPromoted();
    getProjectStats()
      .then((d) => setStats(d.stats))
      .catch(() => {});
    getTagGroups(token)
      .then((groups) => {
        const fieldGroup = groups.find((group) => group.code === "field");
        setFieldOptions(fieldGroup?.tags || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (token) {
      getProjects(token)
        .then(setMineProjects)
        .catch(() => setMineProjects([]));
    } else {
      setMineProjects(null);
    }
  }, [token]);

  const applyFilters = (next: Filters) => {
    setFilters(next);
    setHotList(null);
    setLatestList(null);
    loadAll(next);
  };

  const setField = (fieldTagId?: number) =>
    applyFilters({ ...filters, field_tag_id: fieldTagId });
  const setType = (projectType?: "personal" | "company" | "open") => {
    if (projectType === "open") {
      applyFilters({ ...filters, project_type: undefined, open_source: true });
    } else {
      applyFilters({ ...filters, project_type: projectType, open_source: undefined });
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const results: {
        projects: PlazaProject[];
        pager: any;
        impression_token?: string;
      }[] = [];
      if (hotNext != null) {
        results.push(
          await getProjectRankings({ sort: "hot", page: hotNext, ...filters })
        );
      }
      if (latestNext != null) {
        results.push(
          await getProjectRankings({ sort: "latest", page: latestNext, ...filters })
        );
      }
      let i = 0;
      if (hotNext != null) {
        const data = results[i++];
        setHotList((prev) => [...(prev || []), ...data.projects]);
        setHotNext(data.pager?.next_page ?? null);
      }
      if (latestNext != null) {
        const data = results[i++];
        setLatestList((prev) => [...(prev || []), ...data.projects]);
        setLatestNext(data.pager?.next_page ?? null);
      }
      reportImpressions(results.map((r) => r.impression_token));
    } catch (err: any) {
      showToast(err?.message || "加载失败，请稍后重试");
    } finally {
      setLoadingMore(false);
    }
  };

  const isTopThree = (id: string) => {
    const rank = hotList?.findIndex((p) => p.id === id) ?? -1;
    return rank >= 0 && rank < 3;
  };

  const removeProject = (id: string) => {
    const remove = (list: PlazaProject[] | null) =>
      list?.filter((p) => p.id !== id) ?? list;
    setHotList(remove);
    setLatestList(remove);
  };

  // 地址栏去掉项目弹窗参数（关闭弹窗 / 项目不可见时）
  const clearProjectQuery = () => {
    if (!router.query.project) return;
    const query = { ...router.query };
    delete query.project;
    router.replace(
      { pathname: router.pathname, query },
      undefined,
      { shallow: true }
    );
  };

  const closePopup = () => {
    popupIdRef.current = null;
    setPopupProject(null);
    setPopupLoading(false);
    clearProjectQuery();
  };

  // 打开弹窗：列表点击会带列表项做占位（避免闪烁），深链只有 id
  const openPopupById = (
    id: string,
    placeholder?: PlazaProject | PromotedProject
  ) => {
    popupIdRef.current = id;
    // 地址栏同步当前弹窗对应的项目，保证 /projects?project=<id> 始终可分享
    router.replace(
      { pathname: router.pathname, query: { ...router.query, project: id } },
      undefined,
      { shallow: true }
    );
    setPopupTop(placeholder ? isTopThree(id) : false);
    setPopupProject(placeholder ?? null);
    setPopupUpvoted(false);
    setPopupLoading(true);
    // 详情接口已对已发布项目放开：先取详情（upvoted + 最新计数）再展示，避免状态闪烁
    getProject(token, id)
      .then((detail) => {
        if (popupIdRef.current !== detail.id) return; // 已切换/关闭弹窗，丢弃过期响应
        setPopupLoading(false);
        setPopupProject((prev) =>
          prev && prev.id === detail.id
            ? {
                ...prev,
                ...detail,
                user: detail.user || prev.user,
                upvotes_count: detail.upvotes_count ?? prev.upvotes_count,
                views_count: detail.views_count ?? prev.views_count,
                impressions_count:
                  detail.impressions_count ?? prev.impressions_count,
              }
            : detail
        );
        setPopupUpvoted(Boolean(detail.upvoted));
        // 打开弹窗即上报一次「看过」（后端按指纹/用户去重，每日最多 3 次）
        reportView(id);
      })
      .catch((err: any) => {
        if (popupIdRef.current !== id) return;
        setPopupLoading(false);
        if (err?.statusCode === 404) {
          // 项目已取消发布 / 未展示到广场：从广场移除并关闭弹窗
          removeProject(id);
          closePopup();
          showToast(
            placeholder ? "该项目已取消发布" : "该项目未展示到项目广场"
          );
        }
        // 其他错误：保留弹窗，用列表数据展示
      });
  };

  const openPopup = (project: PlazaProject | PromotedProject) =>
    openPopupById(project.id, project);

  const setUpvoteCount = (id: string, count?: number) => {
    if (count == null) return;
    const update = (list: PlazaProject[] | null) =>
      list?.map((p) =>
        p.id === id ? { ...p, upvotes_count: count } : p
      ) ?? list;
    setHotList(update);
    setLatestList(update);
  };

  const setViewsCount = (id: string, count?: number) => {
    if (count == null) return;
    const update = (list: PlazaProject[] | null) =>
      list?.map((p) => (p.id === id ? { ...p, views_count: count } : p)) ??
      list;
    setHotList(update);
    setLatestList(update);
  };

  // 上报一次「看过」：fire-and-forget，成功后用后端返回的最新计数刷新弹窗与列表
  const reportView = (id: string) => {
    recordProjectView(token, id)
      .then((res) => {
        if (popupIdRef.current !== id) return; // 弹窗已关闭/切换，丢弃过期响应
        if (res?.views_count == null) return;
        setViewsCount(id, res.views_count);
        setPopupProject((prev) =>
          prev && prev.id === id
            ? { ...prev, views_count: res.views_count }
            : prev
        );
      })
      .catch(() => {});
  };

  // 顶成功的提示（PRD）：响应带剩余次数说明本次真正顶上了；没带说明此前已顶过（后端幂等）
  const showUpvotedToast = (remaining?: number) => {
    if (remaining == null) {
      showToast("你已经顶过这个项目了");
    } else if (remaining > 0) {
      showToast(`已顶，今天还可以顶 ${remaining} 个项目`);
    } else {
      showToast("已顶，今天的 3 次用完了");
    }
  };

  const handleUpvote = async (project: PlazaProject | PromotedProject) => {
    if (!token) {
      // 未登录：弹登录，登录后回到 /projects?upvote=<id> 自动完成这次顶
      setLoginRedirect(`/projects?upvote=${project.id}`);
      showAppModal("PhoneSigninModal");
      return;
    }
    if (popupUpvoted) {
      showToast("你已经顶过这个项目了");
      return;
    }
    setUpvoting(true);
    try {
      const res = await upvoteProject(token, project.id);
      setPopupUpvoted(true);
      setUpvoteCount(project.id, res?.upvotes_count);
      setPopupProject((prev) =>
        prev
          ? { ...prev, upvotes_count: res?.upvotes_count ?? prev.upvotes_count + 1 }
          : prev
      );
      showUpvotedToast(res?.upvotes_remaining_today);
    } catch (err: any) {
      // 每日 3 个的限制由后端 400 返回对应文案（「今天的 3 次已经用完，明天再来」）
      showToast(err?.message || "操作失败，请稍后重试");
    } finally {
      setUpvoting(false);
    }
  };

  // 分享的是项目弹窗链接：打开即看到该项目（与审核页项目名链接同一个入口）
  const handleShare = (project: PlazaProject | PromotedProject) => {
    const url = `${window.location.origin}/projects?project=${project.id}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    showToast("已复制项目链接，分享出去让大家来支持你的项目");
  };

  const handleGuestPublish = () => {
    setLoginRedirect("/profile/projects/new");
    showAppModal("PhoneSigninModal");
  };

  // 置顶推广入口前置判断（对齐设计稿 openBoost）：未登录 → 登录；
  // 没有项目 → 去发布；有项目但没展示 → 去个人中心打开展示；都满足才打开弹窗
  const handleOpenBoost = (projectId?: string) => {
    if (!token) {
      setLoginRedirect("/projects");
      showAppModal("PhoneSigninModal");
      return;
    }
    if (mineProjects === null) {
      showToast("项目加载中，请稍后再试");
      return;
    }
    if (!mineProjects.length) {
      setConfirm({
        title: "还没有可以推广的项目",
        content: "置顶推广需要先将项目发布到项目广场，再进行置顶推广。",
        okText: "去发布项目",
        onOk: () => router.push("/profile/projects/new"),
      });
      return;
    }
    const published = mineProjects.filter((p) => p.published);
    if (!published.length) {
      setConfirm({
        title: "项目还没展示到项目广场",
        content: "先在个人中心打开展示开关，再进行置顶推广。",
        okText: "去个人中心",
        onOk: () => router.push("/profile/projects"),
      });
      return;
    }
    setBoostInitialId(
      projectId && published.some((p) => p.id === projectId)
        ? projectId
        : undefined
    );
    setBoostOpen(true);
  };

  // 去充值：先关掉推广弹窗再唤起充值（TopUpModal 层级低于推广弹窗，不能叠着开）；
  // 微信内置浏览器没有支付能力，跳个人资料页充电
  const handleTopUp = () => {
    setBoostOpen(false);
    if (isWechatBrowser()) {
      router.push("/profile/info");
    } else {
      showAppModal("TopUpModal");
    }
  };

  // 下单 / 取消后刷新推广位、排行榜 promoting 标记与我的项目
  const handleBoostChanged = () => {
    loadPromoted();
    loadAll(filters);
    if (token) {
      getProjects(token)
        .then(setMineProjects)
        .catch(() => {});
    }
  };

  // 深链 /projects?project=<id>：直接打开该项目的弹窗（分享链接、审核页、
  // 用户公开页项目卡片都走它）。参数保留在地址栏作为可分享链接
  useEffect(() => {
    if (!router.isReady) return;
    const pid = router.query.project;
    if (typeof pid !== "string" || !pid) return;
    // 弹窗已在打开该项目（打开时地址栏同步会再触发本 effect），不重复拉取
    if (popupIdRef.current === pid) return;

    openPopupById(pid);
  }, [router.isReady, router.query.project, router]);

  // 登录后带着 ?upvote=<id> 返回：自动打开弹窗并完成这次顶
  useEffect(() => {
    if (!router.isReady) return;
    const up = router.query.upvote;
    if (typeof up === "string" && up) {
      pendingUpvote.current = up;
      const query = { ...router.query };
      delete query.upvote;
      router.replace(
        { pathname: router.pathname, query },
        undefined,
        { shallow: true }
      );
    }
  }, [router.isReady, router.query.upvote, router]);

  useEffect(() => {
    const id = pendingUpvote.current;
    if (!id || !token) return;
    if (hotList === null && latestList === null) return;
    pendingUpvote.current = null;
    const project =
      hotList?.find((p) => p.id === id) ||
      latestList?.find((p) => p.id === id);
    if (project) {
      // 直接展示（用户正在主动顶，无需拉详情避免与顶接口竞态）
      popupIdRef.current = project.id;
      setPopupTop(isTopThree(project.id));
      setPopupProject(project);
      setPopupUpvoted(false);
      setPopupLoading(false);
      // 打开弹窗即上报一次「看过」
      reportView(id);
    }
    // 登录后自动完成这次顶（后端幂等，已顶过不会重复计数）
    upvoteProject(token, id)
      .then((res) => {
        setPopupUpvoted(true);
        if (project) {
          setPopupProject((prev) =>
            prev
              ? { ...prev, upvotes_count: res?.upvotes_count ?? prev.upvotes_count + 1 }
              : prev
          );
          setUpvoteCount(id, res?.upvotes_count);
        }
        showUpvotedToast(res?.upvotes_remaining_today);
      })
      .catch((err: any) => {
        // 每日 3 个的限制由后端 400 返回对应文案（「今天的 3 次已经用完，明天再来」）
        showToast(err?.message || "操作失败，请稍后重试");
      });
  }, [hotList, latestList, token]);

  const renderColumn = (
    list: PlazaProject[] | null,
    keyPrefix: string,
    /** 最热列显示看过数，最新列显示发布时间 */
    sub: "views" | "publish"
  ): ReactNode => {
    if (list === null) {
      return <div className={styles.empty2}>加载中…</div>;
    }
    if (list.length === 0) {
      return <div className={styles.empty2}>暂无项目</div>;
    }
    // 前三名强调只作用于「最热」列；「最新」列前三行用普通样式
    const highlightTop = sub === "views";
    return list.map((project, i) => (
      <div
        key={`${keyPrefix}-${project.id}`}
        className={styles.lr}
        onClick={() => openPopup(project)}
      >
        <span
          className={`${styles.lrRk} ${
            highlightTop && i < 3 ? styles.lrRkTop : ""
          }`}
        >
          {i + 1}
        </span>
        <ProjectAvatar
          project={project}
          size="lr"
          top={highlightTop && i < 3}
        />
        <span className={styles.lrBody}>
          <span className={styles.lrName}>
            <b>{project.name}</b>
            {project.promoting && <FlagTop />}
          </span>
          <span className={styles.lrIntro}>{project.description}</span>
        </span>
        <span className={styles.lrNum}>
          <b>
            <UpArrow className={styles.upArw} />
            {formatNum(project.upvotes_count)}
          </b>
          <i>
            {sub === "publish"
              ? formatRelativeTime(project.published_at)
              : `${formatNum(project.views_count)} 看过`}
          </i>
        </span>
      </div>
    ));
  };

  const chipCls = (active: boolean) =>
    active ? `${styles.chip} ${styles.chipActive}` : styles.chip;

  return (
    <SiteLayout pageTitle="项目广场">
      <div className={styles.page}>
        <div className={styles.layout}>
          <main className={styles.main}>
            {/* Hero */}
            <div className={`${styles.card} ${styles.hero}`}>
              <div className={styles.heroL}>
                <h1 className={styles.heroT}>
                  发布你的「<span className={styles.q}>项目</span>」
                </h1>
                <div className={styles.heroS}>
                  会为你带来潜在的合作 / 连接 / 机会
                </div>
              </div>
              <div className={styles.heroN}>
                <div>
                  <b>{formatNum(stats?.published_projects_count)}</b>
                  <span>项目</span>
                </div>
                <div>
                  <b>{formatWan(stats?.total_impressions)}</b>
                  <span>曝光</span>
                </div>
              </div>
            </div>

            {/* 置顶推广中：没有推广中的项目时整块不显示 */}
            {promoted !== null && promoted.length > 0 && (
              <section className={`${styles.card} ${styles.sec}`}>
                <div className={styles.secH}>
                  <span className={styles.secT}>置顶推广中</span>
                  <Button
                    size="sm"
                    className={`${styles.secCta} ${styles.btnBrand}`}
                    onClick={() => handleOpenBoost()}
                  >
                    <svg
                      className={styles.hand}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8.5 11.5V5.4a1.35 1.35 0 0 1 2.7 0v5.4" />
                      <path d="M11.2 10.8V4.1a1.35 1.35 0 0 1 2.7 0v6.7" />
                      <path d="M13.9 10.8V6.6a1.35 1.35 0 0 1 2.7 0v6.9c0 3.6-2.4 6.3-5.8 6.3S5 17.1 5 13.9v-2.2a1.35 1.35 0 0 1 2.7-.2l.3 1.6" />
                    </svg>
                    我也要置顶推广
                  </Button>
                </div>
                <div className={styles.pgrid}>
                  {promoted.map((item) => (
                    <div
                      key={item.id}
                      className={`${styles.pcard} ${
                        isJumpableUrl(item.url) ? "" : styles.isBare
                      }`}
                      onClick={() => openPopup(item)}
                    >
                      <div className={styles.pcTop}>
                        <ProjectAvatar
                          project={item}
                          size="pc"
                          top={isTopThree(item.id)}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className={styles.pcName}>
                            <b>{item.name}</b>
                            <FlagTop />
                          </div>
                        </div>
                      </div>
                      <div className={styles.pcQ}>{item.description}</div>
                      <div className={styles.pcFoot}>
                        <span className={styles.pcMeta}>
                          <b>
                            <UpArrow className={styles.upArw} />
                            {formatNum(item.upvotes_count)}
                          </b>
                          <em>
                            推广期<b>+{formatNum(item.promotion?.upvotes_delta ?? 0)}</b>
                          </em>
                          <i>{formatNum(item.views_count)} 看过</i>
                        </span>
                        {isJumpableUrl(item.url) && (
                          <span className={styles.pcActs}>
                            <a
                              className={styles.pcLink}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(item.url || "", "_blank", "noreferrer");
                              }}
                            >
                              访问项目
                              <ArrRight />
                            </a>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* 只有 1 个推广位时补一个空位占住第二列，中间的浅色竖线才不会消失（移动端单列隐藏） */}
                  {promoted.length === 1 && (
                    <div
                      className={styles.pgridPh}
                      aria-hidden
                    />
                  )}
                </div>
              </section>
            )}

            {/* 项目排行榜 */}
            <section
              className={`${styles.card} ${styles.sec} ${styles.secRank}`}
            >
              <div className={styles.secH}>
                <span className={styles.secT}>项目排行榜</span>
                <span className={styles.secN}>
                  共<b>{formatNum(stats?.published_projects_count)}</b>个项目
                </span>
              </div>

              <div
                className={`${styles.fcard} ${filterShut ? styles.isShut : ""}`}
              >
                <div className={styles.fbRow}>
                  <span className={styles.fbL}>按领域</span>
                  <div className={styles.fbChips}>
                    <button
                      type="button"
                      className={chipCls(filters.field_tag_id == null)}
                      onClick={() => setField(undefined)}
                    >
                      全部
                    </button>
                    {fieldOptions.map((field) => (
                      <button
                        key={field.id}
                        type="button"
                        className={chipCls(filters.field_tag_id === field.id)}
                        onClick={() => setField(field.id)}
                      >
                        {field.name}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.fbMore}
                    onClick={() => setFilterShut(!filterShut)}
                  >
                    {filterShut ? "展开" : "收起"}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                </div>

                {/* 恒渲染：收起态在 PC 由 CSS 隐藏，手机版两行都常显（横向滑动代替裁排） */}
                <div className={`${styles.fbRow} ${styles.fbRowType}`}>
                  <span className={styles.fbL}>按类型</span>
                  <div className={styles.fbChips}>
                    <button
                      type="button"
                      className={chipCls(
                        filters.project_type == null &&
                          filters.open_source == null
                      )}
                      onClick={() => setType(undefined)}
                    >
                      全部
                    </button>
                    <button
                      type="button"
                      className={chipCls(filters.project_type === "personal")}
                      onClick={() => setType("personal")}
                    >
                      个人项目
                    </button>
                    <button
                      type="button"
                      className={chipCls(filters.project_type === "company")}
                      onClick={() => setType("company")}
                    >
                      公司项目
                    </button>
                    <button
                      type="button"
                      className={chipCls(filters.open_source === true)}
                      onClick={() => setType("open")}
                    >
                      开源项目
                    </button>
                  </div>
                </div>
              </div>

              {/* 手机版二选一切换：PC 上由 CSS 隐藏，两列照旧并列；
                  外层 colTabsBar 负责滚动时吸顶（不透明底色），内层是胶囊本身 */}
              <div className={styles.colTabsBar}>
                <div className={styles.colTabs}>
                  <button
                    type="button"
                    className={`${styles.colTab} ${
                      mobileCol === "hot" ? styles.colTabOn : ""
                    }`}
                    aria-pressed={mobileCol === "hot"}
                    onClick={() => setMobileCol("hot")}
                  >
                    最热
                  </button>
                  <button
                    type="button"
                    className={`${styles.colTab} ${
                      mobileCol === "latest" ? styles.colTabOn : ""
                    }`}
                    aria-pressed={mobileCol === "latest"}
                    onClick={() => setMobileCol("latest")}
                  >
                    最新
                  </button>
                </div>
              </div>

              <div className={styles.lgrid}>
                <div
                  className={`${styles.lcard} ${
                    mobileCol === "latest" ? styles.lcardOff : ""
                  }`}
                >
                  <div className={styles.colH}>
                    <span className={styles.colT}>最热</span>
                    <span className={styles.colD}>近 30 天顶得最多</span>
                  </div>
                  {renderColumn(hotList, "hot", "views")}
                </div>
                <div
                  className={`${styles.lcard} ${
                    mobileCol === "hot" ? styles.lcardOff : ""
                  }`}
                >
                  <div className={styles.colH}>
                    <span className={styles.colT}>最新</span>
                    <span className={styles.colD}>按发布时间</span>
                  </div>
                  {renderColumn(latestList, "latest", "publish")}
                </div>
              </div>

              {(hotNext != null || latestNext != null) && (
                <div className={styles.more2}>
                  <Button
                    className={styles.btnWide}
                    ghost
                    disabled={loadingMore}
                    onClick={handleLoadMore}
                  >
                    {loadingMore ? "加载中..." : "加载更多"}
                  </Button>
                </div>
              )}
            </section>
          </main>

          <aside className={styles.side}>
            {token ? (
              <div className={`${styles.card} ${styles.cardPad}`}>
                <div className={styles.mineH}>
                  <span className={styles.cardTitle}>我的项目</span>
                  <a
                    className={styles.sideAct}
                    onClick={() => router.push("/profile/projects")}
                  >
                    管理<i>›</i>
                  </a>
                </div>
                {mineProjects !== null &&
                  (mineProjects.length ? (
                    mineProjects.slice(0, 3).map((project) => (
                      <div
                        className={styles.mineItem}
                        key={project.id}
                      >
                        <ProjectAvatar
                          project={project}
                          size="mine"
                          top={isTopThree(project.id)}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className={styles.mineName}>{project.name}</div>
                          <div className={styles.mineState}>
                            {getProjectTypeLabel(project.project_type)} ·{" "}
                            <b
                              className={
                                project.published ? styles.on : styles.off
                              }
                            >
                              {project.published ? "已展示" : "未展示"}
                            </b>
                          </div>
                        </div>
                        <div className={styles.mineNum}>
                          <b>
                            {project.published
                              ? formatNum(project.recent_impressions_count ?? 0)
                              : "—"}
                          </b>
                          <span>近 7 天曝光</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.guestT}>
                      还没有项目，发布你的第一个项目吧
                    </div>
                  ))}
                <div className={styles.meBtns}>
                  <Button
                    className={styles.meBtn}
                    onClick={() => router.push("/profile/projects/new")}
                  >
                    发布项目
                  </Button>
                  <Button
                    className={`${styles.meBtn} ${styles.btnBrand}`}
                    onClick={() => handleOpenBoost()}
                  >
                    置顶推广
                  </Button>
                </div>
              </div>
            ) : (
              <div className={`${styles.card} ${styles.cardPad}`}>
                <div className={styles.mineH}>
                  <span className={styles.cardTitle}>发布你的项目</span>
                </div>
                <div className={styles.guestT}>
                  把你在做的项目放到项目广场，被顶得越多，排得越靠前；想合作的人也能顺着项目找到你。
                </div>
                <Button
                  className={styles.btnBlock}
                  onClick={handleGuestPublish}
                >
                  登录并发布项目
                </Button>
              </div>
            )}

            <div className={styles.card}>
              <div className={styles.cardHd}>
                <span className={styles.cardTitle}>项目规则</span>
              </div>
              <div className={styles.rules}>
                <div className={styles.rule}>
                  <div className={styles.ruleQ}>【发布及排序】</div>
                  <ul className={styles.ruleA}>
                    <li>发布项目到项目广场，前 2 个新发布的项目各奖励 3 电量</li>
                    <li>
                      近 30 天内，收到 <RuleArrow /> 的数量决定项目排序
                    </li>
                  </ul>
                </div>
                <div className={styles.rule}>
                  <div className={styles.ruleQ}>【支持与电量】</div>
                  <ul className={styles.ruleA}>
                    <li>
                      每个用户每天可以 <RuleArrow /> 3 个不同的项目，每个项目只能{" "}
                      <RuleArrow /> 1 次
                    </li>
                    <li>
                      <RuleArrow /> 只影响该项目排序，不消耗用户自身任何电量
                    </li>
                  </ul>
                </div>
                <div className={styles.rule}>
                  <div className={styles.ruleQ}>【分享与置顶】</div>
                  <ul className={styles.ruleA}>
                    <li>项目可以通过分享被更多人看到和支持</li>
                    <li>也可以通过「置顶推广」来增加额外曝光</li>
                  </ul>
                </div>
              </div>
            </div>

            <DownloadAppCard />
            <QuickLinksCard />
          </aside>
        </div>
      </div>

      {/* 深链进来时先只有 id，加载中 popupProject 仍为 null，靠 loading 撑起加载态 */}
      {(popupProject || popupLoading) && (
        <ProjectPopup
          project={popupProject}
          top={popupTop}
          upvoted={popupUpvoted}
          upvoting={upvoting}
          loading={popupLoading}
          onUpvote={() => popupProject && handleUpvote(popupProject)}
          onShare={() => popupProject && handleShare(popupProject)}
          onClose={closePopup}
        />
      )}

      <PromotionModal
        open={boostOpen}
        token={token}
        projects={(mineProjects || []).filter((p) => p.published)}
        initialProjectId={boostInitialId}
        onClose={() => setBoostOpen(false)}
        onChanged={handleBoostChanged}
        onTopUp={handleTopUp}
      />

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title || ""}
        content={confirm?.content}
        okText={confirm?.okText || "确定"}
        okDisabled={confirming}
        onOk={async () => {
          if (!confirm) return;
          setConfirming(true);
          try {
            await confirm.onOk();
            setConfirm(null);
          } catch {
            // 跳转类操作不会失败，保险起见保持弹窗打开
          } finally {
            setConfirming(false);
          }
        }}
        onCancel={() => setConfirm(null)}
      />
      <Toast text={toast} />
    </SiteLayout>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user.user,
  token: state.user.token,
});

const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators({ showAppModal, setLoginRedirect }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Page);
